import type { CategoryPrompt } from "../types";

import React from "react";

import {
  CalendarDays,
  Cat,
  Dog,
  Microscope,
  PackageOpen,
  PawPrint,
  Wheat,
} from "lucide-react";

export const petsPrompts: CategoryPrompt[] = [
  {
    icon: <Dog />,
    title: "Pet Profile Quizzes",
    description:
      "Build a quiz calculating nutritional needs based on breed, age, weight, and activity, then recommend a specific product along with a feeding guide.",
    prompt: `**Build a pet profile quiz that calculates precise nutritional needs based on individual factors and recommends tailored products with personalized feeding guidance.**

**QUIZ FOCUS:** e.g., "Find Your Dog's Perfect Food" OR "Custom Cat Nutrition Plan" OR "Your Pet's Ideal Diet"

**CALCULATION FACTORS:** e.g., Breed, age, weight, activity level, health conditions (10-25 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] New pet parents (need guidance on nutrition)
- [ ] Health-conscious (optimizing pet diet)
- [ ] Problem-solvers (addressing weight, allergies, energy)

**Tone & Style:**
- Caring pet nutritionist
- Science-backed recommendations
- Individual pet focus
- "Every pet is unique" personalization

**Quiz Question Guidelines:** Each question should:
- Gather specific pet data (breed, weight, age)
- Address activity and health factors
- Build toward caloric/nutritional needs
- Use pet parent friendly language
- 8-10 total questions maximum

**Email Gate Strategy:**
- Place after question #6 (after core data collected)
- Frame as "Get your pet's personalized nutrition plan"
- Offer incentive: "Unlock plan + 20% off first order"
- Single field: email only
- Trust line: "Join 500,000+ pet parents feeding smarter"

**Results Page Elements:**
- **Personalized Headline:** "[Pet Name]'s custom nutrition plan - here's what they need"
- **Pet Profile Summary:** Age, weight, breed, activity level recap
- **Daily Caloric Needs:** Calculated calories per day based on factors
- **Recommended Product:** Specific formula matching their needs
- **Why This Formula:** Protein level, ingredients, life stage appropriateness
- **Feeding Guide:**
  - Cups/grams per day
  - Split into meals (2x or 3x daily)
  - Adjustment guidelines as weight changes
  - Transition timeline from current food
- **Nutritional Breakdown:** Protein, fat, fiber, calories per serving
- **Key Ingredients:** Why each matters for this pet's profile
- **Product Details:** Formula benefits, ingredient quality, manufacturing standards
- **Weight Management:** If overweight/underweight, specific guidance
- **Product Cards Include:** Image, pet suitability, feeding amount, "Start Feeding"
- **Match Reasoning:** "For a [age] [breed] at [weight], this formula provides..."
- **Alternative Options:** Different protein sources or formulas if preferences vary
- **Transition Plan:** Week-by-week mixing schedule from old to new food
- **Portion Calculator:** Interactive tool to adjust for weight changes
- **Subscription Option:** Auto-delivery timed to consumption rate
- **Treats & Supplements:** Complementary products for this pet
- **CTAs:** "Start This Formula" and "Subscribe & Save"
- **Retake Option:** "Pet's needs changed? Recalculate"

**Conversion Tactics Use:**
- Scientific calorie calculation
- Veterinarian-backed formulation
- Personalized feeding amounts
- Weight goal tracking
- Subscription synced to bag duration
- Transition guidance (reduces hesitation)

**Key Quiz Questions to Include:**
- **Pet Type:** Dog, cat, specific species needs
- **Breed:** Size category impacts metabolism
- **Age:** Puppy/kitten, adult, senior (different needs)
- **Current Weight:** Exact pounds/kilograms
- **Ideal Weight:** If different from current
- **Activity Level:** Sedentary, moderate, active, highly active
- **Health Conditions:** Allergies, sensitivities, medical issues
- **Current Diet:** What they eat now, portion size
- **Spayed/Neutered:** Affects caloric needs
- **Special Needs:** Grain-free, limited ingredient, prescription considerations

**Goal:** Convert pet parents into confident, loyal customers by providing scientifically-calculated nutritional recommendations tailored to their individual pet's needs with precise feeding guidance that eliminates guesswork, builds trust through personalization, and increases subscription adoption through consumption-synced auto-delivery.`,
  },
  {
    icon: <Cat />,
    title: "Pet Problem Listicle Advertorial",
    description:
      "Build a listicle addressing common pet problems, educating while positioning my products as solutions with CTAs.",
    prompt: `**Build a pet problem listicle that identifies common health or behavior issues and positions your products as the nutritional solutions with expert backing.**

**TOPIC:** e.g., "7 Signs Your Dog's Food Is Wrong for Them" OR "5 Health Issues Poor Nutrition Causes in Cats" OR "Why Your Pet Has Low Energy"

**PRODUCTS FEATURED:** e.g., "Our senior formula solves problem #3" OR "Our sensitive stomach blend fixes issue #5"

**Reader Type:** Choose one:
- [ ] Problem-aware pet parents (noticing symptoms)
- [ ] Health-concerned (want to prevent issues)
- [ ] Frustrated owners (tried everything, nothing works)

**Tone & Style:**
- Compassionate pet health advocate
- Educational veterinary knowledge
- Empathetic to pet parent worry
- Solution-focused positivity

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("End Digestive Issues")
- Use pet benefit language ("Help Them Thrive")
- Include expert backing ("Vet-Formulated")
- Emphasize care ("Give Them What They Need")

**Conversion Tactics Use:**
- Symptom recognition validation
- Nutritional deficiency explanations
- Veterinarian quotes and endorsements
- Before/after pet transformations
- Ingredient targeting for problems
- Success stories from similar situations

**Key Elements to Include:**
- **Hook:** Relatable pet problem moment (2-3 paragraphs on worried pet parent)
- **List Items (6-9 problems):** Numbered issue → nutritional cause → your solution
- **Problem Structure Per Item:**
  - Problem headline: "#X: [Common pet symptom/issue]"
  - What it looks like: Detailed symptom description
  - Nutritional root cause: What deficiency/imbalance causes this
  - Long-term risks: What happens if not addressed
  - The solution: Your product with specific nutritional fix
  - Key ingredients: What nutrients address the problem
  - How it works: Biological mechanism of improvement
  - Expected timeline: When pet parents see improvement
  - Veterinarian insight: Expert quote on this issue
  - Success story: Pet parent whose pet improved
  - Pet photo: Before/after or thriving pet image
- **Product Callouts:** Nutritional highlights with "Help Your Pet" CTA
- **Ingredient Spotlights:** Deep dives on key nutrients (omega-3s, probiotics, etc.) (2-3 throughout)
- **Vet Endorsements:** Professional validation throughout
- **Nutritional Science:** Why specific ingredients solve problems
- **Quality Standards:** Sourcing, manufacturing, testing
- **Multi-Problem Solutions:** Products addressing several issues
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Safe for all breeds? Prescription needed? Transition time? Picky eaters? Guaranteed results?)

**Goal:** Convert concerned pet parents into loyal customers by helping them recognize nutritional causes behind their pet's problems, then demonstrating how your scientifically-formulated products provide targeted solutions backed by veterinary expertise and real success stories that prove better nutrition leads to healthier, happier pets.`,
  },
  {
    icon: <Microscope />,
    title: "Ingredient Education Landing Page",
    description:
      "Create an educational page decoding ingredient labels and explaining formulations, comparing my premium formula to mainstream brands in a table.",
    prompt: `**Build an ingredient education landing page that teaches pet parents to decode labels while positioning your premium formula as transparently superior to mainstream brands.**

**EDUCATION FOCUS:** e.g., "How to Read Pet Food Labels" OR "What's Really in Your Pet's Food" OR "Premium vs. Cheap: The Truth"

**FORMULA COMPARISON:** e.g., Your premium ingredients vs. generic/mainstream brands

**Target Audience:** Choose one:
- [ ] Label-readers (scrutinize ingredients)
- [ ] Quality-seekers (want premium nutrition)
- [ ] Confused shoppers (don't understand labels)

**Tone & Style:**
- Educational pet nutritionist
- Empowering through knowledge
- Honest industry exposé
- "You deserve to know" transparency

**CTA Guidelines:** Each CTA should:
- Reference quality ("Feed Premium Quality")
- Use transparency language ("See Full Ingredients")
- Include education benefit ("Know What You're Feeding")
- Emphasize pet health ("Give Them the Best")

**Conversion Tactics Use:**
- Label reading tutorial
- Ingredient quality tiers explained
- Your full ingredient disclosure
- Side-by-side brand comparisons
- Hidden filler exposure
- Veterinary nutritionist validation

**Key Elements to Include:**
- **Hero Section:** "Decode Pet Food Labels - Know What You're Really Feeding" headline, "Learn & Shop Better" CTA
- **Why This Matters:** Ingredient quality impacts health (2-3 paragraphs on importance)
- **Label Reading Guide:**
  - How ingredients are listed (by weight)
  - What to look for first 5 ingredients
  - Red flag ingredients to avoid
  - Marketing tricks that mislead
  - Guaranteed analysis explained
  - AAFCO statements decoded
- **Ingredient Quality Tiers:**
  - Premium/human-grade
  - Standard/feed-grade
  - By-products and fillers
  - What each tier means for nutrition
- **Ingredient Breakdown (10-15 common ingredients):**
  - Chicken vs. chicken meal vs. chicken by-product
  - Whole grains vs. grain fragments
  - Named fats vs. generic "animal fat"
  - Preservatives: natural vs. synthetic
  - Protein sources quality comparison
- **What to Avoid List:** 8-12 ingredients that signal low quality
- **Premium Formula Comparison Chart:**
  | Ingredient Category | Mainstream Brands | Our Premium Formula |
  | First ingredient | Corn/grain | Named meat protein |
  | Protein source | By-products, unnamed | Whole meats, named sources |
  | Fillers | Yes (corn, wheat gluten) | None |
  - **Comparison spans 8-12 rows covering key categories**
- **Full Ingredient Disclosure:** Your complete formula with each ingredient explained
- **Why Each Ingredient:** Purpose and benefit for pet health
- **Sourcing Transparency:** Where ingredients come from, supplier standards
- **Manufacturing Standards:** Quality control, testing, certifications
- **Hidden Filler Exposure:** Common cheap ingredients brands hide
- **Veterinary Nutritionist Quote:** Expert validation of premium formulation
- **Cost Per Day Analysis:** Premium vs. mainstream actual feeding cost
- **Health Impact:** Long-term benefits of quality ingredients
- **CTAs:** Hero, after comparison chart, with products (5-6 total)
- **FAQ:** 5-7 questions (Really worth premium price? All ingredients listed? Organic? Grain-free debate? How to transition?)

**Goal:** Convert ingredient-conscious pet parents into premium customers by educating them on label reading, exposing mainstream brand shortcuts, and proving through transparent comparison that your premium ingredients deliver superior nutrition worth the investment for their pet's long-term health and vitality.`,
  },
  {
    icon: <Wheat />,
    title: "Science-Based Education Pages",
    description:
      "Build a science-based page addressing controversial topics, citing expert sources, and helping owners make informed decisions.",
    prompt: `**Build a science-based education page that addresses controversial pet nutrition topics with expert sources to help owners make evidence-informed decisions.**

**CONTROVERSIAL TOPIC:** e.g., "Grain-Free Debate: What Science Says" OR "Raw vs. Cooked: The Evidence" OR "Breed-Specific Nutrition: Fact or Fiction?" OR "Kibble vs. Fresh Food"

**EDUCATIONAL APPROACH:** e.g., Evidence-based, balanced perspective, source-cited

**Target Audience:** Choose one:
- [ ] Research-driven pet parents (want scientific truth)
- [ ] Confused by conflicting info (need clarity)
- [ ] Decision-paralyzed (overwhelmed by debate)

**Tone & Style:**
- Objective scientific educator
- Balanced and non-dogmatic
- Evidence-based not opinion-based
- "Here's what research shows" clarity

**CTA Guidelines:** Each CTA should:
- Reference evidence ("Science-Backed Choice")
- Use informed decision language ("Make Your Choice")
- Include research quality ("Vet-Approved")
- Emphasize pet wellbeing ("Best for Your Pet")

**Conversion Tactics Use:**
- Peer-reviewed research citations
- Veterinary expert quotes
- Both sides of debate presented
- Your product's position explained
- Research quality breakdown
- FDA/AAFCO regulatory guidance

**Key Elements to Include:**
- **Hero Section:** Topic headline as question, "Get the Science" CTA
- **Why This Matters:** Why this topic is controversial and important (2-3 paragraphs)
- **The Controversy Explained:** Both sides of debate laid out fairly
- **What We Know from Research:**
  - Peer-reviewed study findings (5-10 key studies)
  - Sample sizes and study quality
  - Consensus vs. conflicting evidence
  - What's proven vs. what's theoretical
- **Expert Perspectives:**
  - Veterinary nutritionist quotes (3-5)
  - Board-certified veterinarian input
  - Academic researcher insights
  - Professional organization statements
- **Regulatory Position:** FDA, AAFCO, or relevant body guidance
- **The Evidence:**
  - Pro argument with citations
  - Con argument with citations
  - Neutral interpretation
  - Gaps in current research
- **What This Means for Your Pet:**
  - Individual variation considerations
  - When one approach may be better
  - Red flags to watch for
  - How to monitor your pet's response
- **Our Position:** Where your brand stands with scientific justification
- **How We Address This:** Your product's formulation approach and why
- **Research Citations:** Formatted list of all sources (15-30 studies/sources)
- **Myth vs. Fact Section:** Common misconceptions debunked
- **Decision Framework:** Questions to ask when choosing
- **Consultation Recommendation:** When to talk to vet before deciding
- **Further Reading:** Links to research, vet resources, studies
- **CTAs:** Hero, after our position, with products (4-5 total)
- **FAQ:** 6-8 questions (What should I choose? Safe to switch? Research quality? Your approach why? Vet approve? Individual differences?)

**Goal:** Convert research-driven and confused pet parents into informed, confident customers by providing balanced, science-based education on controversial topics that builds trust through objectivity, then clearly explaining your evidence-based formulation approach that helps them make the right decision for their individual pet's needs.`,
  },
  {
    icon: <CalendarDays />,
    title: "Problem-First Landing Page",
    description:
      "Create a page leading with common pet problems, explaining causes and solutions, with before/after photos from customers.",
    prompt: `**Build a problem-first landing page that leads with a specific pet issue, explains causes, and positions your product as the proven solution with real transformations.**

**PET PROBLEM:** e.g., "Is Your Dog Constantly Itching?" OR "Does Your Cat Have Digestive Issues?" OR "Low Energy in Your Senior Dog?" OR "Dull Coat and Skin Problems?"

**YOUR SOLUTION:** e.g., "Our sensitive skin formula" OR "Digestive health blend"

**Target Audience:** Choose one:
- [ ] Problem sufferers (pet currently struggling)
- [ ] Frustrated owners (tried everything)
- [ ] Concerned parents (want to help their pet)

**Tone & Style:**
- Empathetic to pet parent worry
- Compassionate about pet suffering
- Solution-focused and hopeful
- "You're not alone, we can help"

**CTA Guidelines:** Each CTA should:
- Reference problem relief ("Stop the Itching")
- Use outcome language ("Help Them Feel Better")
- Include proof element ("Vet-Approved Solution")
- Emphasize pet comfort ("Give Them Relief")

**Conversion Tactics Use:**
- Problem validation and empathy
- Root cause scientific explanation
- Your nutritional solution mechanism
- Before/after customer pet photos
- Timeline expectations
- Veterinarian endorsements

**Key Elements to Include:**
- **Hero Section:** Problem headline as question, suffering pet image, "Get Relief" CTA
- **You're Not Alone:** Statistics on how common this problem is
- **The Problem Detailed (2-4 paragraphs):** What pet parents see, how it affects quality of life
- **Signs & Symptoms:** Checklist of indicators (8-12 items)
- **Root Causes Explained:**
  - Nutritional deficiencies
  - Ingredient sensitivities
  - Poor quality proteins/fats
  - Common allergens
  - Digestive issues
- **Why Traditional Solutions Fail:** Medications mask, don't solve; wrong foods perpetuate
- **The Nutritional Solution:** Your product with key mechanism
- **How It Works:**
  - Specific ingredients targeting problem
  - Nutritional science explanation
  - What changes in pet's body
  - Timeline for improvement
- **Key Ingredients (3-5):**
  - Ingredient name
  - What it does for this problem
  - Research backing
  - Therapeutic levels in formula
- **Before/After Gallery (8-15):**
  - Customer pet photos showing improvement
  - Pet name, age, breed
  - Problem they had
  - Timeline to improvement (weeks/months)
  - Owner testimonial
  - Current status
- **Expected Timeline:** Week-by-week what to expect
- **Success Stories (5-8):** Detailed testimonials from pet parents
- **Veterinarian Endorsement:** Professional validation of approach
- **What Makes Ours Different:** Comparison to other "solutions"
- **Satisfaction Guarantee:** "See improvement in 60 days or money back"
- **Transition Guide:** How to switch foods safely
- **Additional Support:** Diet tips, environmental factors, when to see vet
- **CTAs:** Hero, after how it works, with gallery, guarantee section (5-6 total)
- **FAQ:** 6-8 questions (How long to work? Safe for my pet? Guarantee details? Works for all breeds? Vet approval needed? Picky eaters?)

**Goal:** Convert worried, frustrated pet parents into hopeful customers by deeply validating their pet's suffering, explaining the nutritional root cause with science, and proving through authentic before/after transformations that your formula delivers real relief backed by veterinary approval and money-back guarantee.`,
  },
  {
    icon: <PackageOpen />,
    title: "Life-Stage Specific Landing Page",
    description:
      "Build a page targeting specific life stages, explaining health science, and featuring transformation testimonials from similar pets.",
    prompt: `**Build a life-stage specific landing page that addresses unique nutritional needs and health science for that age with transformation testimonials from similar pets.**

**LIFE STAGE:** e.g., "Puppy Nutrition" OR "Senior Dog Health" OR "Adult Cat Maintenance" OR "Kitten Development"

**STAGE-SPECIFIC NEEDS:** e.g., Growth support, joint health, cognitive function, energy levels

**Target Audience:** Choose one:
- [ ] New stage entrants (just transitioned to this age)
- [ ] Proactive planners (preparing for next stage)
- [ ] Problem-aware (noticing age-related issues)

**Tone & Style:**
- Life-stage expert guidance
- Age-appropriate empathy
- Science-backed but accessible
- "Every stage matters" care focus

**CTA Guidelines:** Each CTA should:
- Reference life stage ("Perfect for Seniors")
- Use age benefit language ("Support Healthy Aging")
- Include stage-appropriate outcome ("Maintain Vitality")
- Emphasize specialized nutrition ("Formulated for This Stage")

**Conversion Tactics Use:**
- Life-stage health science education
- Age-specific nutritional requirements
- Veterinary gerontology/pediatric validation
- Before/after from same-aged pets
- Preventative health messaging
- Stage transition guidance

**Key Elements to Include:**
- **Hero Section:** Life stage headline, age-appropriate pet image, "Feed for This Stage" CTA
- **Why This Stage Is Unique:** Biological changes happening (2-3 paragraphs)
- **Health Science for This Age:**
  - Metabolic changes
  - Nutritional requirement shifts
  - Common health concerns
  - Preventative care opportunities
  - Organ function considerations
- **Nutritional Needs Breakdown:**
  - Protein requirements (higher/lower than other stages)
  - Fat and calorie needs
  - Essential nutrients for this age
  - Supplements beneficial (glucosamine, DHA, etc.)
  - What to avoid at this stage
- **Common Life-Stage Problems:**
  - Puppies/Kittens: Growth issues, immune development, digestive sensitivity
  - Adults: Weight management, energy maintenance, dental health
  - Seniors: Joint health, cognitive decline, organ support
- **Our Formula for This Stage:**
  - Specific product designed for age
  - Key ingredients and why they matter now
  - Caloric density appropriate for stage
  - Therapeutic nutrients included
  - Easy digestibility considerations
- **Ingredient Spotlight (4-6):**
  - Age-specific nutrients
  - Why critical at this life stage
  - Research on benefits
  - Levels in our formula
- **Life-Stage Testimonials (6-10):**
  - Pet name, age, breed
  - What improved since switching
  - Before/after photos
  - Timeline of transformation
  - Owner observations
  - Current thriving status
- **Age Progression Support:** How to transition to next life stage
- **Veterinarian Insights:** Expert quotes on this life stage nutrition
- **Feeding Guide:** Stage-appropriate portions and frequency
- **Health Monitoring:** What to watch for at this age
- **When to Transition:** Signs it's time for next life stage formula
- **Comparison Chart:** Life stage needs vs. all-ages food (5-7 rows)
- **Success Metrics:** "85% of senior dogs show improved mobility"
- **CTAs:** Hero, after formula details, with testimonials (5-6 total)
- **FAQ:** 6-8 questions (When to switch? Transition timing? Works for all breeds this age? Health conditions? Vet approval? Combination feeding?)

**Goal:** Convert life-stage aware pet parents into loyal customers by educating them on their pet's unique age-related nutritional needs, demonstrating how your stage-specific formula scientifically addresses those needs, and proving through same-aged pet transformations that proper life-stage nutrition delivers measurable health improvements and longevity benefits.`,
  },
  {
    icon: <PawPrint />,
    title: "Pet Photo Galleries",
    description:
      "Create a UGC gallery where pet parents submit photos organized by breed, age, and concern, with filtering for diverse representation.",
    prompt: `**Build a pet photo gallery showcasing real pets organized by breed, age, and health concern with robust filtering for relatable representation.**

**GALLERY FOCUS:** e.g., "Thriving Pets in Our Community" OR "Real Pets, Real Results" OR "Meet Our Happy Pack"

**FILTER CATEGORIES:** e.g., By breed, by age, by health improvement, by product used

**Gallery Visitor Type:** Choose one:
- [ ] Social proof seekers (want to see similar pets)
- [ ] Breed-specific researchers (my breed success stories)
- [ ] Transformation hunters (proof it works for problems)

**Tone & Style:**
- Celebratory pet community
- Authentic and unfiltered
- Inclusive of all breeds/sizes/ages
- "Your pet belongs here" welcoming

**CTA Guidelines:** Each CTA should:
- Use community language ("Join Our Pack")
- Reference relatability ("See Pets Like Yours")
- Include shopping ease ("Shop What They Eat")
- Encourage participation ("Share Your Pet")

**Conversion Tactics Use:**
- Filter by exact breed/age/concern match
- Product tags on pet photos
- Health improvement stories
- Verified customer badges
- Photo submission incentive
- Before/after transformation pairs

**Key Elements to Include:**
- **Hero Section:** Headline, diverse pet photo grid preview, filter options, "Submit Your Pet" CTA
- **Gallery Stats:**
  - "50,000+ happy pets featured"
  - "200+ breeds represented"
  - "95% report health improvements"
- **Filter Options:**
  - By breed (searchable dropdown, 100+ breeds)
  - By age (puppy/kitten, adult, senior)
  - By size (small, medium, large, giant)
  - By health concern (allergies, weight, energy, coat, digestion)
  - By product used
  - Transformation photos only
  - With testimonials only
- **Photo Grid Layout:** Hover shows pet details (name, breed, age, owner name, product used)
- **Featured Pet Stories (8-12):**
  - Multiple pet photos (3-5 per pet)
  - Pet name, breed, age, weight
  - Health journey or transformation
  - Product(s) they eat
  - Owner testimonial (100-200 words)
  - Before/after if transformation
  - Timeline of improvement
  - "Shop [Pet Name]'s Food" CTA
- **Breed Galleries:** Dedicated sections for popular breeds with many examples
- **Age-Specific Collections:** Puppy/kitten gallery, senior success stories
- **Transformation Spotlight:** Before/after pairs with health improvements
- **Health Concern Success Stories:** Filtered views by specific issue solved
- **Product-Specific Views:** See all pets thriving on specific formula
- **Diversity Showcase:** All breeds, sizes, mixed breeds celebrated equally
- **Pet Parent Quotes:** Short testimonials about results
- **Shopping Integration:** Product tags on photos, "Shop This Formula"
- **Community Stats:** Breeds represented, age ranges, success rates
- **Photo Submission Section:** "Share your pet & get 25% off next order"
- **Authenticity Indicators:** Verified customer badges, real pet confirmation
- **Social Proof:** "Join 50,000+ pets thriving on our food"
- **CTAs:** Top of page, with featured stories, submission section (5-6 total)
- **FAQ:** 3-4 questions (How to submit? Photo requirements? Privacy? Multiple pets? Incentive?)

**Goal:** Convert hesitant pet parents into confident buyers by showcasing authentic, diverse pet community that proves products work across all breeds, ages, and health concerns - with powerful filtering that lets them find pets exactly like theirs thriving on your food, backed by real transformation stories and inclusive representation.`,
  },
];
